import {
  returnDecimalFormatting,
  formatValueWithNotation,
} from "@/lib/globalFormatterHelper";
import { NotationToDecimalValueType } from "@/lib/interfaces and Enums/TYPENotations";
import {
  containsHyphenInQuote,
  formatDollarFractionString,
} from "@/lib/businessRules/dollarFractionBusinessRule";
import {
  getDealtPriceFromData,
  getNotationFromParams,
  shouldHideDealtPrice,
} from "./dealtPriceHelpers";

/**
 * Format value only (for copy/paste - NO notation symbols).
 * Applies notation-based rounding but does not include notation suffix.
 *
 * @param price - The dealt price value (string from API)
 * @param notation - The notation type for determining decimal places
 * @returns Formatted string with value only (no notation suffix)
 */
export function formatDealtPriceValue(
  price: string | undefined,
  notation?: NotationToDecimalValueType,
): string {
  if (price === null || price === undefined || price === "") {
    return "";
  }

  // $fr: return raw value for copy/paste (Z-digit stays as number, e.g., "116-023")
  if (containsHyphenInQuote(price)) {
    return price;
  }

  const numPrice = parseFloat(price);
  if (isNaN(numPrice)) {
    return price;
  }

  // Apply notation-based rounding
  return returnDecimalFormatting(numPrice, notation) || "";
}

/**
 * Format with notation for display.
 * Applies notation-based rounding AND includes notation suffix.
 * Only shows notation when there is a value - NOT as a placeholder when empty.
 *
 * @param price - The dealt price value (string from API)
 * @param notation - The notation type for determining decimal places and suffix
 * @returns Formatted string for display with notation suffix, or empty string if no value
 */
export function formatDealtPriceWithNotation(
  price: string | undefined,
  notation?: NotationToDecimalValueType | string,
): string {
  if (price === null || price === undefined || price === "") {
    // Return empty - do NOT show notation as placeholder
    return "";
  }

  const notationSuffix = notation || "";

  // $fr: return the formatted fraction string (e.g., "116-023" → "116-02 3")
  if (containsHyphenInQuote(price)) {
    return formatDollarFractionString(price);
  }

  const numPrice = parseFloat(price);
  if (isNaN(numPrice)) {
    return formatValueWithNotation(price, notationSuffix);
  }

  // Apply notation-based rounding (use "$" for decimal places when notation is a resolved currency)
  const roundingNotation = notation === "$" || notation === "£" || notation === "€" || notation === "¥" || (notation && notation.endsWith(" "))
    ? "$" as NotationToDecimalValueType
    : notation as NotationToDecimalValueType | undefined;
  const formattedValue = returnDecimalFormatting(numPrice, roundingNotation);
  if (!formattedValue) {
    return "";
  }

  return formatValueWithNotation(formattedValue, notationSuffix);
}

/**
 * AG Grid valueFormatter for copy/paste functionality.
 * Returns value with notation rounding but WITHOUT notation suffix.
 *
 * @param params - AG Grid cell params
 * @returns Formatted string for copy/paste (value only, no notation)
 */
export function dealtPriceValueFormatter(params: any): string {
  if (!params.data) {
    return "";
  }

  // Check if dealt price should be hidden
  if (shouldHideDealtPrice(params, params.data.status)) {
    return "";
  }

  const price = getDealtPriceFromData(params.data);
  const notation = getNotationFromParams(params);

  return formatDealtPriceValue(price, notation);
}
